import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip"; // 新增 Tooltip 引入
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { SystemLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import EmissionSourceInfo from "./Components/Emission_Source_Info";
import EnergyUsageInfo from "./Components/Energy_Usage_Info";
import InventoryInfo from "./Components/Inventory_Info";
import ManufacturingInfo from "./Components/Manufacturing_Info";
import ProcessInfo from "./Components/Process_Info";
import PrecursorInfo from "./Components/Precursor_Info";
import ProductOverview from "./Components/Product_Overview_Info";
import { AcctContext } from "../../utility/useReduce/userNameReduce";

const CbamDtail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗
  // 進度條相關
  const [activeIndex, setActiveIndex] = useState(0);
  const stepsContainerRef = useRef(null);
  // 頁籤權限狀態
  const [accessibleTabs, setAccessibleTabs] = useState([]);

  // 設置標記，表示從子頁面返回
  useEffect(() => {
    sessionStorage.setItem("fromCbamChildPage", "true");
  }, []);

  useEffect(() => {
    if (action === "") {
      btnBackTo();
    }
  }, [action]);

  // 載入頁籤權限
  useEffect(() => {
    const loadTabPermissions = () => {
      try {
        // 從全域狀態獲取 F10103 的頁籤權限信息
        const tabPermissions = state?.tabPermissions?.["F10603"];

        if (!tabPermissions || tabPermissions.trim() === "") {
          // 如果沒有權限設定，設為空陣列（無任何權限）
          setAccessibleTabs([]);
          return;
        }

        // 解析頁籤權限字串
        const accessibleTabs = tabPermissions.split("|").filter((tab) => tab.trim() !== "");
        setAccessibleTabs(accessibleTabs);
      } catch (error) {
        // 如果載入失敗，設為空陣列（無任何權限）
        setAccessibleTabs([]);
      }
    };

    loadTabPermissions();
  }, [state?.tabPermissions]); // 依賴頁籤權限變化

  const btnBackTo = () => {
    navigate("/management/cbam");
  };

  const copyToClipboard = (data) => {
    const textToCopy = data;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        handleSuccessMessage(dispatch, t("copy") + " " + t("message.success"));
      })
      .catch((error) => {});
  };

  // 定義步驟配置
  const getSteps = () => {
    const baseSteps = [
      { id: 0, title: t("inventory_information"), icon: "pi pi-info-circle", tabName: "inventory_info" },
      { id: 1, title: t("manufacturingInfo"), icon: "pi pi-globe", tabName: "manufacturing_info" },
      { id: 2, title: t("emission_source"), icon: "pi pi-search", tabName: "emission_source" },
      { id: 3, title: t("energyUsage"), icon: "pi pi-database", tabName: "energy_usage" },
      { id: 4, title: t("process"), icon: "pi pi-calculator", tabName: "process_info" },
      { id: 5, title: t("precursor"), icon: "pi pi-file-word", tabName: "precursor_info" },
      { id: 6, title: t("productOverview"), icon: "pi pi-file-word", tabName: "product_overview" },
      // { id: 6, title: t("productSummary"), icon: "pi pi-file-word" },
    ];

    return baseSteps;
  };

  const steps = getSteps();

  // 智慧縮放：只有在畫面寬度不足時才縮小前面步驟
  useEffect(() => {
    const container = stepsContainerRef.current;
    if (!container) return;

    const updateShrinkState = () => {
      // 1. 先全部移除 shrunk
      const stepBoxes = Array.from(container.querySelectorAll(".step-arrow-box"));
      stepBoxes.forEach((box) => box.classList.remove("shrunk"));

      // 2. 檢查是否需要縮小（畫面寬度嚴重不足時）
      if (container.scrollWidth > container.clientWidth + 50) {
        // 增加一些容錯空間
        // 畫面寬度嚴重不足，縮小前面的步驟
        for (let i = 0; i < activeIndex; i++) {
          if (stepBoxes[i]) {
            stepBoxes[i].classList.add("shrunk");
          }
        }
      }
      // 如果畫面寬度足夠或只是稍微不足，讓滾動條處理
    };

    // 初始更新
    updateShrinkState();

    // 監聽容器大小變化
    const resizeObserver = new ResizeObserver(() => {
      updateShrinkState();
    });
    resizeObserver.observe(container);

    // 清理函數
    return () => {
      resizeObserver.disconnect();
    };
  }, [activeIndex, steps.length]);

  // 檢查步驟是否可用
  const isStepEnabled = (stepIndex) => {
    if (stepIndex === 0) return true; // 第一個步驟總是可用
    if (action === "edit" || action === "detail") return true; // 更新或查看模式所有步驟都可用

    // 創建模式：只有報告書步驟需要特殊處理
    if (stepIndex === steps.length - 1) {
      // 報告書步驟需要完成前幾個步驟才能點擊
      return stepIndex <= activeIndex + 1; // 允許提前一個步驟點擊報告書
    }

    return stepIndex <= activeIndex; // 其他步驟只能訪問已完成的步驟
  };

  // 判斷是否需要縮小：前面步驟且畫面寬度嚴重不足時
  const shouldShrink = (stepIndex) => {
    const container = stepsContainerRef.current;
    if (!container || stepIndex >= activeIndex) return false;

    // 檢查容器是否有嚴重的橫向滾動（增加容錯空間）
    return container.scrollWidth > container.clientWidth + 10;
  };

  // 處理文字截斷
  const truncateText = (text, maxLength = 7) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // 獲取顯示文字
  const getDisplayText = (text, stepIndex) => {
    // 只有在需要縮小時才截斷文字
    if (shouldShrink(stepIndex)) {
      // 縮小狀態下截斷文字
      const maxLength = 8;
      return truncateText(text, maxLength);
    }
    return text;
  };

  // 處理步驟點擊
  const handleStepClick = (stepIndex) => {
    if (isStepEnabled(stepIndex)) {
      setActiveIndex(stepIndex);
      // 如果是報告書步驟，跳轉到報告書頁面
      // if (stepIndex === steps.length - 1) {
      //   handleReportClick();
      // } else {
      //   setActiveIndex(stepIndex);
      // }
    }
  };

  // 處理報告書點擊
  const handleReportClick = () => {
    // 設定報告書相關狀態
    setAction("report");

    // 跳轉到報告書頁面
    navigate("/management/cbam/report", {
      state: {
        companyName: currentCbamMasterSeq,
        activeIndex: activeIndex, // 保存當前步驟位置
      },
    });
  };

  // 檢查特定步驟是否有權限
  const hasStepPermission = (tabName) => {
    return accessibleTabs.includes(tabName);
  };

  // 渲染步驟內容
  const renderStepContent = () => {
    const currentStep = steps[activeIndex];

    if (!currentStep) {
      return <div className="text-center p-4"></div>;
    }

    const tabName = currentStep.tabName;

    // 檢查權限
    if (!hasStepPermission(tabName)) {
      return (
        <div className="text-center p-4">
          <h3>{t("no_permission_title")}</h3>
          <p>{t("no_permission_message")}</p>
        </div>
      );
    }

    switch (tabName) {
      case "inventory_info":
        return <InventoryInfo setActiveIndex={setActiveIndex} />;
      case "manufacturing_info":
        return <ManufacturingInfo setActiveIndex={setActiveIndex} />;
      case "emission_source":
        return <EmissionSourceInfo setActiveIndex={setActiveIndex} />;
      case "energy_usage":
        return <EnergyUsageInfo setActiveIndex={setActiveIndex} />;
      case "process_info":
        return <ProcessInfo setActiveIndex={setActiveIndex} />;
      case "precursor_info":
        return <PrecursorInfo setActiveIndex={setActiveIndex} />;
      case "product_overview":
        return <ProductOverview setActiveIndex={setActiveIndex} />;
    }
  };

  return (
    <>
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              navigate("/management/cbam");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        <div className="align-items-end">
          <label className="">
            {action === "create" ? t("button.create") : t("button.detail")}
            {itemObj?.cbamMasterSeq && (
              <Button
                type="button"
                style={{ alignItems: "flex-start", padding: "0px" }}
                size="small"
                tooltipOptions={{ position: "left" }}
                tooltip={`${t("button.copy")} ${itemObj?.cbamMasterSeq}`}
                text
                onClick={() => {
                  copyToClipboard(currentCbamMasterSeq);
                }}
                icon="pi pi-copy"
              />
            )}
          </label>
        </div>
      </div>

      {/* 上方廠商基本資訊(create 時不顯示) */}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <div className="grid align-items-center">
            <div className="col-10">
              <h3 className="m-0">
                {currentCompany?.companyName} {t("cbamInspection")}
              </h3>
            </div>
            {action === "detail" && (
              <>
                <div className="col-12 lg:col-4">
                  <label className="label-has-icon">{t("product_name")}:</label>
                  <span>{itemObj?.cbamProjectName}</span>
                </div>
                <div className="col-12 lg:col-4">
                  <label className="label-has-icon">{t("place_name")}:</label>
                  <span>{itemObj?.placeName}</span>
                </div>
                <div className="col-12 lg:col-4">
                  <label className="label-has-icon">{t("reportingPeriod")}:</label>
                  <span>
                    {itemObj?.reportingStartDate} ~ {itemObj?.reportingEndDate}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 進度條樣式的分頁顯示 */}
      <div className="p-card">
        <div className="p-card-body">
          {/* 進度條 */}
          <div className="progress-steps mb-4">
            <div ref={stepsContainerRef} className="steps-container">
              {steps.map((step, index) => {
                const statusClass =
                  index === activeIndex
                    ? "active"
                    : index < activeIndex
                    ? "completed"
                    : isStepEnabled(index)
                    ? "enabled"
                    : "disabled";
                const first = index === 0 ? "first" : "";
                const last = index === steps.length - 1 ? "last" : "";
                const shrinkClass = shouldShrink(index) ? "shrunk" : "";
                const stepId = `step-tooltip-${index}`;
                const displayText = getDisplayText(step.title, index);
                const isTruncated = displayText !== step.title;
                return (
                  <div
                    key={step.id}
                    id={stepId}
                    className={`step-arrow-box ${statusClass} ${first} ${last} ${shrinkClass}`}
                    onClick={() => handleStepClick(index)}
                  >
                    {displayText}
                    {/* 只在截斷時顯示 Tooltip */}
                    {isTruncated && <Tooltip target={`#${stepId}`} content={step.title} position="bottom" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 步驟內容 */}
          <div className="step-content">{renderStepContent()}</div>
        </div>
      </div>
    </>
  );
};

export default CbamDtail;
